export type IndexMetricKey =
  | 'sandp_500'
  | 'sandp_400'
  | 'russell_3000'
  | 'nasdaq_100'
  | 'topix'
  | 'hang_seng_index'
  | 'kospi_200'
  | 'csi_300'
  | 'csi_all_share'
  | 'stoxx_europe_600'
  | 's_and_p_tsx_composite'
  | 'nifty_500'
  | 's_and_p_bmv_ipc'
  | 'ibra';

export type IndexRegion =
  | 'United States'
  | 'Asia-Pacific'
  | 'Europe'
  | 'Canada and Latin America';

export type IndexMetricDefinition = {
  key: IndexMetricKey;
  name: string;
  filterLabel: string;
  region: IndexRegion;
  geography: string;
  marketSegment: 'Large Cap' | 'Mid Cap' | 'Small Cap' | 'Broad Market';
  description: string;
};

export const INDEX_REGIONS: IndexRegion[] = [
  'United States',
  'Asia-Pacific',
  'Europe',
  'Canada and Latin America',
];

export const INDEX_METRICS: IndexMetricDefinition[] = [
  {
    key: 'sandp_500',
    name: 'S&P 500',
    filterLabel: 'S&P 500',
    region: 'United States',
    geography: 'United States',
    marketSegment: 'Large Cap',
    description: 'Leading large-cap companies in the United States.',
  },
  {
    key: 'sandp_400',
    name: 'S&P MidCap 400',
    filterLabel: 'S&P MidCap 400',
    region: 'United States',
    geography: 'United States',
    marketSegment: 'Mid Cap',
    description: 'Mid-cap companies in the United States.',
  },
  {
    key: 'russell_3000',
    name: 'Russell 2000',
    filterLabel: 'Russell 2000',
    region: 'United States',
    geography: 'United States',
    marketSegment: 'Small Cap',
    description: 'Small-cap companies in the United States.',
  },
  {
    key: 'nasdaq_100',
    name: 'Nasdaq-100',
    filterLabel: 'Nasdaq-100',
    region: 'United States',
    geography: 'United States',
    marketSegment: 'Large Cap',
    description: 'Large non-financial companies listed on the Nasdaq stock exchange.',
  },
  {
    key: 'topix',
    name: 'TOPIX',
    filterLabel: 'TOPIX (Japan)',
    region: 'Asia-Pacific',
    geography: 'Japan',
    marketSegment: 'Broad Market',
    description: 'A broad benchmark for companies listed in Japan.',
  },
  {
    key: 'hang_seng_index',
    name: 'Hang Seng Index',
    filterLabel: 'Hang Seng (Hong Kong)',
    region: 'Asia-Pacific',
    geography: 'Hong Kong',
    marketSegment: 'Large Cap',
    description: 'Leading companies listed in Hong Kong.',
  },
  {
    key: 'kospi_200',
    name: 'KOSPI 200',
    filterLabel: 'KOSPI 200 (South Korea)',
    region: 'Asia-Pacific',
    geography: 'South Korea',
    marketSegment: 'Large Cap',
    description: 'Leading companies listed in South Korea.',
  },
  {
    key: 'csi_300',
    name: 'CSI 300',
    filterLabel: 'CSI 300 (China)',
    region: 'Asia-Pacific',
    geography: 'China',
    marketSegment: 'Large Cap',
    description: 'Large-cap companies listed in Shanghai and Shenzhen.',
  },
  {
    key: 'csi_all_share',
    name: 'CSI All Share',
    filterLabel: 'CSI All Share (China)',
    region: 'Asia-Pacific',
    geography: 'China',
    marketSegment: 'Broad Market',
    description: 'A broad benchmark for companies listed in mainland China.',
  },
  {
    key: 'nifty_500',
    name: 'Nifty 500',
    filterLabel: 'Nifty 500 (India)',
    region: 'Asia-Pacific',
    geography: 'India',
    marketSegment: 'Broad Market',
    description: 'A broad benchmark for companies listed in India.',
  },
  {
    key: 'stoxx_europe_600',
    name: 'STOXX Europe 600',
    filterLabel: 'STOXX Europe 600',
    region: 'Europe',
    geography: 'Europe',
    marketSegment: 'Broad Market',
    description: 'Large-, mid-, and small-cap companies across developed European markets.',
  },
  {
    key: 's_and_p_tsx_composite',
    name: 'S&P/TSX Composite',
    filterLabel: 'S&P/TSX Comp. (Canada)',
    region: 'Canada and Latin America',
    geography: 'Canada',
    marketSegment: 'Broad Market',
    description: 'A broad benchmark for companies listed in Canada.',
  },
  {
    key: 's_and_p_bmv_ipc',
    name: 'S&P/BMV IPC',
    filterLabel: 'S&P/BMV IPC (Mexico)',
    region: 'Canada and Latin America',
    geography: 'Mexico',
    marketSegment: 'Large Cap',
    description: 'Leading companies listed in Mexico.',
  },
  {
    key: 'ibra',
    name: 'IBrA',
    filterLabel: 'IBrA (Brazil)',
    region: 'Canada and Latin America',
    geography: 'Brazil',
    marketSegment: 'Broad Market',
    description: 'A broad benchmark for companies listed in Brazil.',
  },
];

export const INDEX_METRIC_KEYS = INDEX_METRICS.map(index => index.key);

export const INDEX_METRIC_LABELS: Record<string, string> = INDEX_METRICS.reduce(
  (labels, index) => ({ ...labels, [index.key]: index.name }),
  {}
);

export const indexMetricLabel = (metric: string): string | undefined =>
  INDEX_METRIC_LABELS[metric];
